import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

// Import SEO components (lazy-loaded for performance)
const SEOMetadataList = React.lazy(() => import('./SEOMetadataList'));
const SEOMetadataEditor = React.lazy(() => import('./SEOMetadataEditor'));
const SEOPreviewTool = React.lazy(() => import('./SEOPreviewTool'));
const SEOMetadataWhatsNew = React.lazy(() => import('./SEOMetadataWhatsNew'));

const seoMetadataModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: 'seometadata',
  name: 'SEO Manager',
  description: 'Manage SEO metadata, social sharing, and page optimization',
  version: '1.3.0',
  enabled: true,
  category: 'marketing',
  order: 15,
  icon: '🔍',
  color: '#805AD5', // Purple

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      path: '/seometadata',
      component: SEOMetadataList,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/seometadata/new',
      component: SEOMetadataEditor,
      permissionLevel: 'create',
      useLayout: true,
    },
    {
      path: '/seometadata/preview',
      component: SEOPreviewTool,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/seometadata/edit/:pagePath',
      component: SEOMetadataEditor,
      permissionLevel: 'edit',
      useLayout: true,
    },
    {
      path: '/seometadata/whats-new',
      component: SEOMetadataWhatsNew,
      permissionLevel: 'view',
      useLayout: true,
    },
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime
  navigation: [
    {
      label: 'All SEO Metadata',
      path: '/seometadata',
      icon: '📄',
      permissionLevel: 'view',
    },
    {
      label: 'Add SEO Metadata',
      path: '/seometadata/new',
      icon: '➕',
      permissionLevel: 'create',
    },
    {
      label: 'SEO Preview Tool',
      path: '/seometadata/preview',
      icon: '👁️',
      permissionLevel: 'view',
    },
    {
      label: "What's New",
      path: '/seometadata/whats-new',
      icon: '🎉',
      permissionLevel: 'view',
    },
  ],

  // Permissions come from the backend (single source of truth).
  // See: business-builder-backend/src/modules/seometadata/permissions.ts
  // These empty arrays get replaced at runtime by ModulePermissionsContext.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: 'Add SEO Metadata',
      path: '/seometadata/new',
      icon: '➕',
      description: 'Create SEO metadata for a new page',
    },
  ],

  // CHANGELOG
  changelog: [
    {
      version: '1.3.0',
      releaseDate: '2025-12-17',
      title: 'Hierarchical Path Fallback System',
      features: [
        '📂 Hierarchical fallback: /section/page inherits from /section, then from /',
        '🎯 Section landing pages can set default images for all sub-pages',
        '🔄 Three-level inheritance: exact path → parent path → root path',
        '🌐 Dynamic domain detection from brandConfig (no more hardcoded domains)',
        '🏷️ Site name and descriptions pulled from brandConfig automatically',
        '⚡ Efficient caching with parallel GraphQL queries',
        '📖 Updated documentation with inheritance examples',
      ],
      notes: 'Major enhancement to the fallback system. Now SEO metadata inherits hierarchically through path segments. For example, /businessbuilder/tokenomics will inherit social images from /businessbuilder if not set, and fall back to / as the final default. Perfect for section landing pages that want consistent branding across all sub-pages.',
    },
    {
      version: '1.2.0',
      releaseDate: '2025-12-17',
      title: 'Default Site SEO & Smart Fallbacks',
      features: [
        '⭐ Homepage "/" route now serves as the default fallback for all pages',
        '🖼️ Social media images (OG, Twitter, LinkedIn, Pinterest) inherit from "/" when not set',
        '🏷️ "Site Default" badge clearly marks the "/" route in the list view',
        '⚠️ Warning notification when default SEO metadata is not configured',
        '📢 Alert when "/" exists but is not published',
        '🔗 Quick action button to set up or publish default SEO',
        '💡 Helpful tooltips explaining the fallback system',
      ],
      notes: 'New smart fallback system ensures all pages have proper social media previews even without individual configuration. Set up your homepage SEO once and it automatically applies as the default for Facebook, Twitter, LinkedIn, and Pinterest sharing across your entire site.',
    },
    {
      version: '1.1.0',
      releaseDate: '2025-11-23',
      title: 'Image Upload System & Enhanced Previews',
      features: [
        '🖼️ Drag-and-drop image upload for all social media platforms',
        '🖼️ Click-to-browse image selection with preview',
        '📤 Real-time upload progress tracking',
        '✅ Image validation (type and 10MB size limit)',
        '🎯 Platform-specific image recommendations (dimensions)',
        '✅ Enhanced SEO Preview with all 5 platforms (Google, Facebook, Twitter, LinkedIn, Pinterest)',
        '🔍 Standalone SEO Preview Tool with URL parsing',
        '🎨 Preview cards showing how content appears on each platform',
        '⚡ Secure image uploads with permission checks',
      ],
      notes: 'Major update adding complete image management system with drag-and-drop interface. Upload social media images directly from the SEO editor with real-time previews showing how your content will appear on Google, Facebook, Twitter, LinkedIn, and Pinterest. Platform-specific dimension recommendations ensure optimal appearance across all channels.',
    },
    {
      version: '1.0.0',
      releaseDate: '2025-11-23',
      title: 'Initial Release - Complete SEO Management System',
      features: [
        '✅ SEO metadata list view with search and filtering',
        '✅ Comprehensive editor with Basic SEO, Social Sharing, and Advanced tabs',
        '✅ Open Graph tags for Facebook, LinkedIn, and Pinterest',
        '✅ Twitter Card metadata support',
        '✅ Real-time preview component for Google and social platforms',
        '✅ JSON-LD structured data editor',
        '✅ Character count warnings for meta titles and descriptions',
        '✅ Publish/unpublish functionality with status indicators',
        '✅ Bulk import support for multiple pages',
        '✅ SEOHead component for easy integration on any page',
        '✅ Backend GraphQL API with 36/36 passing unit tests',
        '✅ Multi-tenant architecture with tenant isolation',
        '✅ Permission-based access control',
        '✅ MongoDB storage with unique page path indexing',
      ],
      notes: 'First stable release of the SEO Manager module with complete functionality for managing SEO metadata, social sharing tags, and structured data across all pages.',
    },
    {
      version: '0.1.5',
      releaseDate: '2025-11-13',
      title: 'Alpha Release - Early Preview',
      features: [
        '🔧 Basic SEO metadata creation and editing',
        '🔧 Simple page path and meta title/description fields',
        '🔧 GraphQL backend with basic CRUD operations',
        '🔧 Multi-tenant data isolation',
        '🔧 Basic list view for SEO entries',
      ],
      notes: 'Very early alpha version with minimal functionality. This was the initial proof-of-concept demonstrating basic SEO metadata management capabilities. Many features were still in development.',
    },
  ],

  // ROADMAP
  roadmap: [],
};

export default seoMetadataModuleConfig;
